import { useCallback, useState, useEffect, useRef } from 'react';
import { useMsal } from 'src/auth/firebaseMsalCompat';
import axios, { isAxiosError } from 'axios';

import { loginRequest } from '../config/authConfig';
import { getApiHost } from '../utils/getApiHost';
import { useFirebaseAuth } from '../auth/FirebaseAuthContext';
import {
  getSubscriptionStatus,
  invalidateSubscriptionStatus,
  type RawSubscriptionStatus,
} from '../services/subscriptionStatus';
import { trackError } from '../services/telemetry';

export interface ScheduledPlanChange {
  newTier: 'starter' | 'pro';
  newBillingPeriod: 'monthly' | 'annual';
  effectiveDate: string; // ISO format date string
}

export interface SubscriptionStatus {
  status: string;
  creditBalance?: number;
  extraCredits?: number; // Credits from credit pack purchases (never expire)
  subscriptionTier?: 'free' | 'starter' | 'pro'; // Business tracked via userType
  billingPeriod?: 'monthly' | 'annual';
  userType?: 'personal' | 'business';
  scheduledPlanChange?: ScheduledPlanChange; // Scheduled downgrade or billing period change
  // Which provider the active subscription resolves from. 'apple'/'google' mean
  // the subscription was purchased in the mobile app and must be managed there.
  source?: 'stripe' | 'apple' | 'google';
  // Business API trial end (ISO) and job quota. Absent / null when the
  // account has no trial or no limit.
  businessTrialEndsAt?: string | null;
  businessCreditLimit?: number | null;
  businessCreditsUsed?: number | null;
}

/**
 * Whether we actually know the caller's entitlement.
 *
 * A nullable status cannot express the difference between "still fetching" and
 * "the fetch failed", and every place that collapsed them ended up showing a
 * subscriber something wrong: a spinner that never resolves, or a plan labelled
 * free. Mirror of `resolveEntitlement` in
 * `MobileApp/stores/subscriptionRules.ts`.
 *
 * - `pending` — nothing fetched yet, or in flight. Say nothing.
 * - `known`   — we have a snapshot. Classify normally.
 * - `failed`  — errored with nothing to fall back on. Show an error and a retry;
 *               never a price, a cap, or a tier label.
 */
export type Entitlement = 'pending' | 'known' | 'failed';

export const resolveEntitlement = (
  status: SubscriptionStatus | null,
  loading: boolean,
  error: Error | null
): Entitlement => {
  if (status) return 'known';
  if (loading) return 'pending';
  return error ? 'failed' : 'pending';
};

/**
 * `current_period_end` arrives as a unix timestamp and the cache hands back a
 * shared, read-only object, so copy before transforming.
 */
const toSubscriptionStatus = (raw: RawSubscriptionStatus): SubscriptionStatus =>
  ({
    ...raw,
    current_period_end: new Date(
      (raw.current_period_end ?? 0) * 1000
    ).toLocaleDateString(),
  }) as SubscriptionStatus;

/**
 * Reads the caller's subscription status.
 *
 * A failed read sets `statusError` and leaves `subscriptionStatus` null. Does
 * not trip the error boundary. Treat null as "unknown", not "free", and offer
 * `retryStatus`.
 */
export const useSubscriptionStatus = (isAuthenticated: boolean) => {
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<Error | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const { instance, inProgress } = useMsal(); // inProgress is to track the login state
  const { user } = useFirebaseAuth();
  const accountUid = user && !user.isAnonymous ? user.uid : null;
  const uid = isAuthenticated ? accountUid : null;
  const uidRef = useRef(uid);
  uidRef.current = uid;

  /**
   * Forced re-read, for callers that must not act on an unknown entitlement.
   *
   * Returns the status so a caller mid-flow (e.g. starting a checkout) can act on
   * the answer immediately instead of waiting for a re-render, and resolves to
   * null when we still don't know — which such callers must treat as "stop", not
   * as "no subscription".
   */
  const refreshStatus =
    useCallback(async (): Promise<SubscriptionStatus | null> => {
      const requestUid = uidRef.current;
      if (!requestUid) return null;
      try {
        setStatusLoading(true);
        setStatusError(null);
        const authResult = await instance.acquireTokenSilent({
          ...loginRequest,
        });
        // Never answer an explicit retry from the cache: the user asked because
        // what they were shown was missing or wrong.
        const data = toSubscriptionStatus(
          await getSubscriptionStatus(authResult.idToken, requestUid, {
            force: true,
          })
        );
        if (uidRef.current !== requestUid) return null;
        setSubscriptionStatus(data);
        return data;
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error(String(err));
        const status = isAxiosError(err) ? err.response?.status : undefined;
        trackError(error, { source: 'useSubscriptionStatus.refresh', status });
        if (uidRef.current === requestUid) setStatusError(error);
        return null;
      } finally {
        if (uidRef.current === requestUid) setStatusLoading(false);
      }
    }, [instance]);

  const retryStatus = useCallback(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    // Wait until auth has finished resolving before calling the API.
    if (inProgress !== 'none') return;

    if (!accountUid) {
      invalidateSubscriptionStatus();
      setSubscriptionStatus(null);
      setStatusError(null);
      setStatusLoading(false);
      return;
    }

    if (!uid) {
      setSubscriptionStatus(null);
      setStatusError(null);
      setStatusLoading(false);
      return;
    }

    let cancelled = false;
    // Drop the previous account immediately. The cache is keyed by uid, but
    // this state would otherwise keep showing it until the new read returns.
    setSubscriptionStatus(null);
    setStatusError(null);
    setStatusLoading(true);

    const init = async () => {
      try {
        const authResult = await instance.acquireTokenSilent({
          ...loginRequest,
        });
        const apiUrl = getApiHost();
        const headers = { Authorization: `Bearer ${authResult.idToken}` };

        // If returning from Stripe Checkout, fulfill the session first
        // so the DB is updated before we read subscription status.
        const sessionId = new URLSearchParams(window.location.search).get(
          'session_id'
        );

        if (sessionId) {
          try {
            await axios.post(
              `${apiUrl}/user/fulfill-checkout`,
              { session_id: sessionId },
              { headers }
            );
          } catch (err) {
            // Non-fatal: the webhook may still fulfill it.
            // Log and continue to fetch status regardless.
            console.warn(
              'fulfill-checkout failed, falling back to status fetch:',
              err
            );
          }

          // Clean up the session_id URL parameter
          if (!cancelled) {
            const url = new URL(window.location.href);
            url.searchParams.delete('session_id');
            window.history.replaceState({}, '', url.toString());
          }
        }

        // Shared, deduplicated fetch (retries on 404 internally). Force a
        // fresh read after fulfilling a checkout so the cache — and the
        // NavBar badge subscribed to it — reflect the new plan.
        const data = toSubscriptionStatus(
          await getSubscriptionStatus(authResult.idToken, uid, {
            force: Boolean(sessionId),
          })
        );

        if (!cancelled) setSubscriptionStatus(data);
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error(String(err));
        const status = isAxiosError(err) ? err.response?.status : undefined;
        trackError(error, { source: 'useSubscriptionStatus', status });
        if (!cancelled) setStatusError(error);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [instance, isAuthenticated, inProgress, uid, accountUid]);

  return {
    subscriptionStatus,
    statusLoading,
    statusError,
    // Gate anything that prices, caps or labels on `entitlement === 'known'`.
    entitlement: resolveEntitlement(
      subscriptionStatus,
      statusLoading,
      statusError
    ),
    retryStatus,
    refreshStatus,
    setSubscriptionStatus,
  };
};
